import { useCallback } from 'react';
import { TEST_PAGE } from '@constants/segment';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import pushRoute from '@utils/pushRoute';
import { appendQuizReturnTo } from '@utils/quizReturnPath';
import { SUBSCRIPTION_TOGGLE } from '../Common/SubscriptionToggle';
import {
  buildTestTabBaseProps,
  getRandomTestSectionLabel,
  saveTestTabAnalyticsContext,
  trackTestRandomTestCtaClicked,
  trackTestRandomTestSelected,
  trackTestSubscriptionSegmentClicked,
  trackTestTabCheckoutStarted,
  trackTestTestCardClicked,
  trackTestTestSeriesViewed
} from './testTabAnalytics';

const useTestTabEventHandlers = ({
  asPath,
  goalUID,
  goalSlug,
  goalDisplayName,
  isLoggedIn,
  isTestTabV2,
  activeTestTab,
  activeCategoryTab,
  appliedFilters,
  listingFilterConfig,
  isPlusTabSelected,
  openPrimeTrialPromoModal
}) => {
  const getBaseTestTabProps = useCallback(
    (
      view = activeTestTab,
      categoryTabId = activeCategoryTab,
      filters = appliedFilters
    ) =>
      buildTestTabBaseProps({
        view,
        categoryTabId,
        appliedSelections: filters,
        isTestTabV2
      }),
    [activeCategoryTab, activeTestTab, appliedFilters, isTestTabV2]
  );

  const handleSubscriptionToggle = useCallback(
    (toggleValue) => {
      trackTestSubscriptionSegmentClicked({
        goalUid: goalUID,
        goalName: goalDisplayName,
        filterValue:
          toggleValue === SUBSCRIPTION_TOGGLE.PLUS_ICONIC
            ? 'Plus & Iconic'
            : 'Prime',
        ...getBaseTestTabProps()
      });
    },
    [getBaseTestTabProps, goalDisplayName, goalUID]
  );

  const handleRandomTestToggle = useCallback(
    (action) => {
      trackTestRandomTestCtaClicked({
        goalUid: goalUID,
        goalName: goalDisplayName,
        action,
        ...getBaseTestTabProps()
      });
    },
    [getBaseTestTabProps, goalDisplayName, goalUID]
  );

  const handleRecommendedUpsell = useCallback(
    (plan, meta = {}) => {
      const lastPrimarySourceSection =
        meta.scope === 'section'
          ? getRandomTestSectionLabel(meta.durationBucket)
          : 'Random Test Exhausted';

      if (plan === 'prime' && !isPlusTabSelected) {
        openPrimeTrialPromoModal(
          PrimeTrialPromoContextualId.TEST,
          goalUID,
          goalSlug,
          {
            dismissNavigateTo: null,
            analyticsContext: {
              analyticsPitchSource: lastPrimarySourceSection
            }
          }
        );
        return;
      }

      trackTestTabCheckoutStarted({
        goalUid: goalUID,
        goalName: goalDisplayName,
        lastPrimarySourceSection
      });
      pushRoute(
        `/goal/${goalSlug}/${goalUID}/subscriptions`,
        undefined,
        undefined,
        TEST_PAGE
      );
    },
    [
      goalDisplayName,
      goalSlug,
      goalUID,
      isPlusTabSelected,
      openPrimeTrialPromoModal
    ]
  );

  const handlePlusStickyCheckout = useCallback(() => {
    trackTestTabCheckoutStarted({
      goalUid: goalUID,
      goalName: goalDisplayName,
      lastPrimarySourceSection: 'Test Page Bottom Banner'
    });
  }, [goalDisplayName, goalUID]);

  const handleTestCardClick = useCallback(
    (card) => {
      const baseProps = getBaseTestTabProps();

      if (card.source === 'recommended') {
        trackTestRandomTestSelected({
          goalUid: goalUID,
          goalName: goalDisplayName,
          duration: card.duration || card.title,
          durationBucket: card.durationBucket,
          testUid: card.id,
          testName: card.title,
          ...baseProps
        });
        saveTestTabAnalyticsContext({
          ...baseProps,
          test_uid: card.id,
          test_name: card.title,
          is_nta_test: card.isCbtTest ?? false
        });

        if (card.href) {
          pushRoute(
            card.href.startsWith('/quiz')
              ? appendQuizReturnTo(card.href, asPath)
              : card.href
          );
        }
        return;
      }

      if (card.variant !== 'test_series') {
        trackTestTestCardClicked({
          goalUid: goalUID,
          goalName: goalDisplayName,
          card,
          appliedSelections: appliedFilters,
          filterConfig: listingFilterConfig,
          ...baseProps
        });
      }

      if (
        isTestTabV2 &&
        isLoggedIn &&
        (card.lockType === 'plus' ||
          (card.lockType === 'prime' && isPlusTabSelected))
      ) {
        trackTestTabCheckoutStarted({
          goalUid: goalUID,
          goalName: goalDisplayName,
          lastPrimarySourceSection: 'Locked Test Card'
        });
        pushRoute(
          `/goal/${goalSlug}/${goalUID}/subscriptions`,
          undefined,
          undefined,
          TEST_PAGE
        );
        return;
      }

      if (card.lockType === 'prime' && isLoggedIn && !isPlusTabSelected) {
        openPrimeTrialPromoModal(
          PrimeTrialPromoContextualId.TEST,
          goalUID,
          goalSlug,
          {
            dismissNavigateTo: null,
            analyticsContext: {
              testUid: card.variant === 'quiz' ? card.id : undefined
            }
          }
        );
        return;
      }

      if (card.variant === 'test_series') {
        trackTestTestSeriesViewed({
          goalUid: goalUID,
          goalName: goalDisplayName,
          ...baseProps,
          test_series_title: card.title,
          test_series_uid: card.id
        });
        saveTestTabAnalyticsContext({
          ...baseProps,
          test_series_title: card.title,
          test_series_uid: card.id,
          is_nta_test: card.isCbtTest ?? false
        });
      } else {
        saveTestTabAnalyticsContext({
          ...baseProps,
          test_uid: card.id,
          test_name: card.title,
          is_nta_test: card.isCbtTest ?? false
        });
      }

      if (card.href) {
        const shouldAttachReturnPath =
          card.variant === 'quiz' && card.href.startsWith('/quiz');

        pushRoute(
          shouldAttachReturnPath
            ? appendQuizReturnTo(card.href, asPath)
            : card.href
        );
      }
    },
    [
      appliedFilters,
      asPath,
      getBaseTestTabProps,
      goalDisplayName,
      goalSlug,
      goalUID,
      isLoggedIn,
      isPlusTabSelected,
      isTestTabV2,
      listingFilterConfig,
      openPrimeTrialPromoModal
    ]
  );

  return {
    handleSubscriptionToggle,
    handleRandomTestToggle,
    handleRecommendedUpsell,
    handlePlusStickyCheckout,
    handleTestCardClick
  };
};

export default useTestTabEventHandlers;
